// =============================================================================
// DeclineReasonModal.jsx — capture a responder's reason for Declined / Needs Info
// =============================================================================
// When a responder sets an item to Declined or Needs Info, this draggable modal
// captures the reason the DFV Service will be told. The reason is REQUIRED (Add is
// disabled until text is entered); Cancel / close reverts the decision (handled by
// the caller). Max 500 chars. The captured reason is held in memory and folded into
// the approval-summary comment at Confirm & Send. (John, 30 Jul 2026.)
//
// REASON TYPE (db-207 decision log, Oct 2026): a REQUIRED dropdown above the text,
// for reporting only — the DFV Service never sees it. Options come from Manage →
// DFV Services → Dropdown Lists → "Decision Reasons" (lookup 'decision_reason'). The
// built-in list below shows straight away and is replaced by the managed list once
// it loads; if that load fails or comes back empty the built-in list stays, so the
// pop-up can never get stuck. onAdd(reason, reasonType).
// =============================================================================
import React from "react";
import { listLookupOptions } from "../../data/lookups";
import { ModalBackdrop } from "../form/FormUI.jsx";

const MAX = 500;

// Same values as the db-207 seed, used only if the managed list can't be loaded.
export const FALLBACK_DECISION_REASONS = [
  "Device hire available / cheaper",
  "Not required for this year level",
  "For next school year",
  "Exceeds activity limit",
  "Cost too high",
  "Already covered",
  "Not eligible under policy",
  "Information or documents needed",
  "Duplicate request",
  "Other",
];

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  marginBottom: 6,
};

export function DeclineReasonModal({
  status,
  itemLabel,
  initial = "",
  initialCategory = "",
  onAdd,
  onCancel,
}) {
  const [text, setText] = React.useState(initial || "");
  const [category, setCategory] = React.useState(initialCategory || "");
  const [options, setOptions] = React.useState(FALLBACK_DECISION_REASONS);
  const trimmed = text.trim();
  const canAdd = !!trimmed && !!category;

  React.useEffect(function () {
    let live = true;
    listLookupOptions("decision_reason")
      .then(function (rows) {
        const values = (rows || [])
          .map(function (r) {
            return r?.value;
          })
          .filter(Boolean);
        if (live && values.length) setOptions(values);
      })
      .catch(function () {
        // keep the built-in list
      });
    return function () {
      live = false;
    };
  }, []);

  // Keep a pre-filled type selectable even if it has since been deactivated.
  const shown =
    initialCategory && !options.includes(initialCategory)
      ? options.concat([initialCategory])
      : options;
  const title = status === "Declined" ? "Reason for declining" : "More information needed — reason";

  return (
    <ModalBackdrop
      draggable
      dragTitle={title}
      onClose={onCancel}
      contentStyle={{ width: 540, maxWidth: "92vw", background: "#fff" }}
    >
      <div style={{ padding: 20 }}>
        {itemLabel && (
          <div style={{ fontSize: 13, color: "#607D79", marginBottom: 10, fontWeight: 600 }}>
            {itemLabel}
          </div>
        )}
        <label htmlFor="decline-reason-type" style={labelStyle}>
          Reason type (for reporting — not shown to the DFV Service)
        </label>
        <select
          id="decline-reason-type"
          value={category}
          onChange={function (e) {
            setCategory(e.target.value);
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #CFD8DC",
            fontSize: 14,
            fontFamily: "inherit",
            color: "#1A2E2B",
            background: "#fff",
            marginBottom: 14,
          }}
        >
          <option value="">Choose a reason type…</option>
          {shown.map(function (v) {
            return (
              <option key={v} value={v}>
                {v}
              </option>
            );
          })}
        </select>
        <label htmlFor="decline-reason" style={labelStyle}>
          Reason (the DFV Service will see this)
        </label>
        <textarea
          id="decline-reason"
          value={text}
          rows={5}
          maxLength={MAX}
          // biome-ignore lint/a11y/noAutofocus: the modal opens for this single input
          autoFocus
          placeholder="e.g. This item isn't eligible under current policy…"
          onChange={function (e) {
            setText(e.target.value.slice(0, MAX));
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #CFD8DC",
            fontSize: 14,
            fontFamily: "inherit",
            resize: "vertical",
            color: "#1A2E2B",
            background: "#fff",
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 12, color: text.length >= MAX ? "#C62828" : "#607D79" }}>
            {text.length}/{MAX}
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: "#fff",
                color: "#1A2E2B",
                border: "1px solid #CFE0DE",
                borderRadius: 8,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canAdd}
              onClick={function () {
                onAdd(trimmed, category);
              }}
              style={{
                background: "#00695C",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 700,
                cursor: canAdd ? "pointer" : "not-allowed",
                opacity: canAdd ? 1 : 0.5,
                fontFamily: "inherit",
              }}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
